/*
 * @Author: niuqingxia
 * @Date: 2024-12-19 10:50:18
 * @Last Modified by: mikey.zhaopeng
 * @Last Modified time: 2024-12-Tu 09:47:53
 筛选框组件(提供日期、省、省市、省市区的筛选功能)
 */

<template>
  <div class="filter-block">
    <!-- 日期 -->
    <template v-if="filterType === 'date'">
      <el-date-picker
        v-model="datePick"
        class="filter-item"
        :clearable="false"
        :disabled="filterUiDisabled"
        :style="{
          backgroundColor: config.chartSet.chartStyle.backgroundColor,
          borderColor: config.chartSet.chartStyle.borderColor,
          fontSize: config.chartSet.chartStyle.fontSize + 'px',
          color: config.chartSet.chartStyle.color,
        }"
        type="monthrange"
        align="right"
        value-format="yyyy-MM-dd"
        unlink-panels
        range-separator="-"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        @change="handleSearch">
      </el-date-picker>
    </template>

    <!-- 省、省市、省市区 -->
    <template v-if="filterType !== 'date'">
      <el-cascader
        v-model="province"
        class="filter-item"
        :placeholder="filterAreaMap.get(filterType).placholder"
        clearable
        :options="showRegionInfo"
        :props="{
          value: 'code',
          multiple: false,
          emitPath: false,
          label: 'name',
          children: 'children',
          checkStrictly: false,
        }"
        :filterable="$route.path.includes('/view/')"
        :style="{
          backgroundColor: config.chartSet.chartStyle.backgroundColor,
          border: `1px solid ${config.chartSet.chartStyle.borderColor}`,
          borderRadius: '2px',
          fontSize: config.chartSet.chartStyle.fontSize + 'px',
          color: config.chartSet.chartStyle.color,
          width: '100%',
        }"
        :disabled="filterUiDisabled"
        @change="handleSearch">
      </el-cascader>
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import Request from 'src/utils/request'
import ChartMixin from '../mixin'
import { orderFilterDateRange, toFilterDateOnly } from '@/utils/filterDateRange'

export default {
  name: 'FilterBlock',
  mixins: [ChartMixin],
  props: {
    config: {
      type: Object,
    },
    componentType: {
      type: String,
    },
    globalConfig: {
      type: Object,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const initFilterForm = {
      startTime: '',
      endTime: '',
      provinceCode: '',
      cityCode: '',
      regionCode: '',
    }
    const filterAreaMap = new Map([
      ['province', { placholder: '省', level: 1 }],
      ['city', { placholder: '省市', level: 2 }],
      ['region', { placholder: '省市区', level: 3 }],
    ])
    const initDate = [
      dayjs().subtract(6, 'month').format('YYYY-MM-') + '01',
      dayjs().format('YYYY-MM-DD'),
    ]
    return {
      // 默认筛选时间
      initDate,
      datePick: [...initDate],
      initFilterForm: _.cloneDeep(initFilterForm),
      filterForm: _.cloneDeep(initFilterForm),
      provinceList: [],
      // 日期选择快捷选项
      pickerOptions: {
        shortcuts: [{
          text: '本月',
          onClick (picker) {
            const end = new Date()
            const start = dayjs(dayjs().format('YYYY-MM') + '-01').toDate()
            picker.$emit('pick', [start, end])
          },
        }, {
          text: '最近一个月',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            picker.$emit('pick', [start, end])
          },
        }, {
          text: '最近三个月',
          onClick (picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
            picker.$emit('pick', [start, end])
          },
        }, {
          text: '今年',
          onClick (picker) {
            const end = new Date()
            const start = new Date(new Date().getFullYear() + '-01-01')
            picker.$emit('pick', [start, end])
          },
        }],
      },
      province: [],
      filterAreaMap,
      showRegionInfo: [],
    }
  },
  mounted () {
    if (this.hasUsableGlobalFilterParams()) {
      this.initFromGlobalScreenFilterParams()
    } else {
      this.applyPersistedChartDataToUi()
    }
    this.maybeLoadIndicatorDefaults()
  },
  computed: {
    ...mapGetters(['regionInfo']),
    /** 指标库模式下筛选值由指标回填，不允许手动点开编辑 */
    filterUiDisabled () {
      return !!this.isDisabled || this.config?.chartSet?.dataSource === 'indicatorLib'
    },
    filterType () {
      return this.config.chartSet.chartStyle.filterType
    },
    combinedVars () {
      return [this.regionInfo, this.filterType]
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
  },
  watch: {
    'config.chartSet.dataSource' (v) {
      if (v === 'indicatorLib') {
        this.$nextTick(() => this.maybeLoadIndicatorDefaults())
      }
    },
    'config.chartSet.chartStyle.filterType' () {
      if (this.config.chartSet?.dataSource === 'indicatorLib' && this.selectedIndicatorIdList?.length) {
        this.getChartData(this.filterParams || {}).catch(() => {})
      }
    },
    combinedVars: {
      handler (newValue) {
        const [regionInfo, filterType] = newValue
        if (filterType === 'date') {
          this.showRegionInfo = []
          return
        }
        if (regionInfo) {
          const level = this.filterAreaMap.get(filterType).level
          if (level === 3) {
            this.showRegionInfo = _.cloneDeep(regionInfo)
          } else {
            const data = this.getChildTree(regionInfo, level)
            this.showRegionInfo = _.cloneDeep(data)
          }
          // 区划树异步就绪后再绑一次 v-model，避免 init 时 options 为空导致级联不回填
          this.$nextTick(() => this.rebindRegionCascaderFromFilterForm())
        }
      },
      immediate: true,
    },
  },
  created () {
    this.$store.dispatch('district/getRegionInfo')
  },
  beforeDestroy () {
    this.datePick = ''
    this.province = ''
    this.filterForm = _.cloneDeep(this.initFilterForm)
    // 查看/预览页：销毁时不再 emit。handleSearch 会向父级抛筛选，跳转大屏时易与异步 init 竞态并覆盖地图
    const path = this.$route && this.$route.path
    const isViewLike = path && (path.includes('/screen/view') || path === '/screen/preview')
    if (!isViewLike) {
      this.handleSearch()
    }
  },
  methods: {
    /** 从已保存的 chartViewData.chartData（对象）回填 UI，不触发 filterChange */
    applyPersistedChartDataToUi () {
      const raw = this.config?.chartViewData?.chartData
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
      if (!raw.startTime && !raw.provinceCode && !raw.cityCode && !raw.regionCode) return
      this.applyFilterParamsToUi(raw)
    },
    /** 指标拉数后：同步日期/级联并通知父级刷新其它组件 */
    syncFilterUiAndNotify (params) {
      if (!params || typeof params !== 'object') return
      const next = { ...params }
      if (next.startTime) next.startTime = toFilterDateOnly(next.startTime)
      if (next.endTime) next.endTime = toFilterDateOnly(next.endTime)
      if (next.startTime && next.endTime) {
        const o = orderFilterDateRange(next.startTime, next.endTime)
        next.startTime = o.startTime
        next.endTime = o.endTime
      }
      this.applyFilterParamsToUi(next)
      this.$emit('filterChange', {
        ...next,
        addSn: this.config.addSn,
      })
    },
    applyFilterParamsToUi (params) {
      const ft = this.filterType
      if (ft === 'date') {
        const s0 = params.startTime ? toFilterDateOnly(params.startTime) : ''
        const e0 = params.endTime ? toFilterDateOnly(params.endTime) : ''
        if (s0 && e0) {
          const { startTime: s, endTime: e } = orderFilterDateRange(s0, e0)
          this.datePick = [s, e]
          this.filterForm.startTime = s
          this.filterForm.endTime = e
        }
        return
      }
      if (ft === 'province') {
        this.province = params.provinceCode || ''
        this.filterForm.provinceCode = params.provinceCode || ''
        this.filterForm.cityCode = ''
        this.filterForm.regionCode = ''
        return
      }
      if (ft === 'city') {
        this.province = params.cityCode || params.provinceCode || ''
        this.filterForm.provinceCode = params.provinceCode || ''
        this.filterForm.cityCode = params.cityCode || ''
        this.filterForm.regionCode = ''
        return
      }
      if (ft === 'region') {
        this.province = params.regionCode || params.cityCode || params.provinceCode || ''
        this.filterForm.provinceCode = params.provinceCode || ''
        this.filterForm.cityCode = params.cityCode || ''
        this.filterForm.regionCode = params.regionCode || ''
      }
    },
    maybeLoadIndicatorDefaults () {
      if (this.config.chartSet?.dataSource !== 'indicatorLib') return
      if (!this.selectedIndicatorIdList?.length) return
      this.getChartData(this.filterParams || {}).catch(() => {})
    },
    hasUsableGlobalFilterParams () {
      const raw = this.globalConfig?.filterParams
      if (!raw || typeof raw !== 'string') return false
      try {
        const o = JSON.parse(raw)
        return !!(o && typeof o === 'object' && Object.keys(o).length)
      } catch (e) {
        return false
      }
    },
    /** 从大屏接口保存的 filterParams 回填 UI（按 filterType 只动本组件相关字段），并通知父级 */
    initFromGlobalScreenFilterParams () {
      const raw = this.globalConfig?.filterParams
      if (!raw || typeof raw !== 'string') return
      let filterParams
      try {
        filterParams = JSON.parse(raw)
      } catch (e) {
        return
      }
      if (!filterParams || typeof filterParams !== 'object' || !Object.keys(filterParams).length) return

      const merged = { ...filterParams }
      if (merged.startTime) merged.startTime = toFilterDateOnly(merged.startTime)
      if (merged.endTime) merged.endTime = toFilterDateOnly(merged.endTime)
      if (merged.startTime && merged.endTime) {
        const o = orderFilterDateRange(merged.startTime, merged.endTime)
        merged.startTime = o.startTime
        merged.endTime = o.endTime
      }

      this.filterForm = _.cloneDeep({ ...this.initFilterForm, ...merged })
      this.applyFilterParamsToUi(merged)

      this.$emit('filterChange', {
        ...merged,
        addSn: this.config.addSn,
      })
    },
    /** 区划 options 晚于 filterForm 时，再绑一次级联选中值 */
    rebindRegionCascaderFromFilterForm () {
      if (this.filterType === 'date') return
      const { provinceCode, cityCode, regionCode } = this.filterForm || {}
      if (!provinceCode && !cityCode && !regionCode) return
      if (!this.showRegionInfo?.length) return
      if (this.filterType === 'province' && provinceCode) {
        this.applyFilterParamsToUi({ provinceCode, cityCode: '', regionCode: '' })
      } else if (this.filterType === 'city' && (provinceCode || cityCode)) {
        this.applyFilterParamsToUi({ provinceCode, cityCode, regionCode: '' })
      } else if (this.filterType === 'region' && (provinceCode || cityCode || regionCode)) {
        this.applyFilterParamsToUi({ provinceCode, cityCode, regionCode })
      }
    },
    /** 兼容旧调用名 */
    init () {
      this.initFromGlobalScreenFilterParams()
    },
    // 筛选
    async handleSearch () {
      let params
      switch (this.filterType) {
        case 'date':
          {
            const s0 = this.datePick ? toFilterDateOnly(this.datePick[0]) : ''
            const e0 = this.datePick ? toFilterDateOnly(this.datePick[1]) : ''
            const { startTime, endTime } = orderFilterDateRange(s0, e0)
            params = { startTime, endTime }
            this.filterForm.startTime = startTime
            this.filterForm.endTime = endTime
          }

          break
        case 'province':
          this.filterForm.provinceCode = this.province
          this.filterForm.cityCode = ''
          this.filterForm.regionCode = ''
          params = {
            provinceCode: this.province,
            cityCode: '',
            regionCode: '',
          }
          break
        case 'city': {
          const currentPath = this.getPathById(this.showRegionInfo, this.province)
          this.filterForm.provinceCode = currentPath?.[0]?.code
          this.filterForm.cityCode = currentPath?.[1]?.code
          this.filterForm.regionCode = ''
          params = {
            provinceCode: currentPath?.[0]?.code,
            cityCode: currentPath?.[1]?.code,
            regionCode: '',
          }
        }
          break
        case 'region':
          {
            const currentPath = this.getPathById(this.showRegionInfo, this.province)
            this.filterForm.provinceCode = currentPath?.[0]?.code
            this.filterForm.cityCode = currentPath?.[1]?.code
            this.filterForm.regionCode = currentPath?.[2]?.code
            params = {
              provinceCode: currentPath?.[0]?.code,
              cityCode: currentPath?.[1]?.code,
              regionCode: currentPath?.[2]?.code,
            }
          }
          break
        default:
          params = {}
          break
      }
      this.config.chartViewData.chartData = params
      // 自定义与指标库均写入 chartData，便于保存；指标库下用户改选区也会触发全屏数据刷新
      this.$emit('filterChange', {
        ...params,
        addSn: this.config.addSn,
      })
    },
    clearDate () {
      this.filterForm = _.cloneDeep(this.initFilterForm)
    },
    // 获取树的某一层子树
    getChildTree (tree, depth) {
      if (depth <= 0 || !Array.isArray(tree)) return []
      return tree.map(node => {
        const newNode = { name: node.name, code: node.code }
        if (depth > 1 && node.children) {
          newNode.children = this.getChildTree(node.children, depth - 1)
        }
        return newNode
      })
    },
    getPathById (tree, targetId) {
      const path = []
      function traverse (nodes, currentPath) {
        for (const node of nodes) {
          const newPath = [...currentPath, { name: node.name, code: node.code }]
          if (node.code === targetId) {
            path.push(...newPath)
            return true // 找到目标节点，停止递归
          }
          if (node.children && traverse(node.children, newPath)) {
            return true // 如果目标节点在子节点中，停止递归
          }
        }
        return false
      }

      traverse(tree, [])
      return path
    },
    saveData (data) {
      Request({
        url: '/panel/chart_data',
        method: 'post',
        data,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-block{
  height: 100%;
  ::v-deep .filter-item{
    height: 100%;
    width: 100%;
    .el-range-input,.el-range-separator{
      background-color: transparent;
      font-size: inherit;
      color: inherit;
    }
    .el-input{
      font-size: inherit;
      color: inherit;
      height: inherit;
      .el-input__inner{
        background-color: inherit !important;
        border: none;
        font-size: inherit;
        color: inherit;
        height: inherit;
      }
    }
    .el-range-separator{
      display: flex;
      align-items: center;
    }
  }
}
</style>
